import { useEffect, useMemo, useState } from 'react';
import { Platform } from 'react-native';

import { getAttachmentThumbnail } from '../services/attachmentService';
import { colors } from '../theme/colors';
import {
  RICH_HTML_BLOCK_SPACING,
  RICH_HTML_BODY_COLOR,
  RICH_HTML_BODY_FONT_SIZE,
  RICH_HTML_BODY_LINE_HEIGHT,
  RICH_HTML_HEADING_SPACING,
  RICH_HTML_IMG_MAX_HEIGHT,
  RICH_HTML_LIST_ITEM_SPACING,
  RICH_HTML_IMG_MAX_WIDTH,
  RICH_HTML_LINK_COLOR,
  RICH_HTML_MARK_BG,
  RICH_HTML_TABLE_HEADER_BG,
  mentionTokenCss,
  richHtmlScopedCss,
} from './richText/richHtmlVisualContract';
import { RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH } from './richText/richTextWideContent';
import {
  isDisplayableInlineImageSrc,
  pendingInlineImagePreviewSrc,
  rememberPendingInlineImagePreview,
  rewriteHtmlAttachmentRefsToPreviewSrcs,
} from '../services/pendingInlineImagePreview';
import { isRasterImageDataUrl } from '../utils/richContent/inlineImageSrc';
import { isLocalNetworkImgSrc } from '../utils/richContent/localNetworkImgSrc';
import {
  collectRichHtmlAttachmentIds,
  placeholderUnresolvedAttachmentImages,
  replaceRichHtmlAttachmentSrc,
} from '../utils/richContent/richHtmlAttachmentRefs';
import {
  isLegacyPlainTextAreaValue,
  sanitizeTextArea,
} from '../utils/richContent/sanitizeTextArea';

import type { MixedStyleRecord } from 'react-native-render-html';

/** Wide tables / code need a horizontal scroller (1106 issue 3). */
export function richHtmlNeedsHorizontalScroll(html: string): boolean {
  return /<table\b/i.test(html) || /<pre\b/i.test(html);
}

/** Native RenderHTML squeezes to contentWidth; give tables room to pan. */
export function richHtmlNativeOverflowWidth(
  html: string,
  viewportWidth: number,
): number {
  if (!richHtmlNeedsHorizontalScroll(html) || viewportWidth <= 0) {
    return Math.max(viewportWidth, 1);
  }
  const row = html.match(/<tr[\s\S]*?<\/tr>/i)?.[0] ?? html;
  const columns = row.match(/<t[dh]\b/gi)?.length ?? 1;
  return Math.max(viewportWidth, columns * 96);
}

/** Size posted by the native table WebView (`{ w, h }`). */
export function parseRichHtmlWebViewSize(
  raw: string,
): { width: number; height: number } | null {
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') {
      return null;
    }
    const rec = parsed as { w?: unknown; h?: unknown };
    const width = Number(rec.w);
    const height = Number(rec.h);
    if (
      !Number.isFinite(width) ||
      !Number.isFinite(height) ||
      width <= 0 ||
      height <= 0
    ) {
      return null;
    }
    return {
      width: Math.min(Math.ceil(width), RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH),
      height: Math.min(Math.ceil(height), RICH_TEXT_NATIVE_MAX_CONTENT_WIDTH),
    };
  } catch {
    return null;
  }
}

/** Matches Tiptap table chrome in `RichTextEditor.web.tsx` (`.ss-tiptap-danger-room`). */
export const RICH_HTML_TAGS_STYLES: MixedStyleRecord = {
  p: {
    marginTop: 0,
    marginBottom: RICH_HTML_BLOCK_SPACING,
    minHeight: RICH_HTML_BODY_LINE_HEIGHT,
    flexShrink: 1,
    width: '100%',
  },
  blockquote: {
    borderLeftColor: '#9CA3AF',
    borderLeftWidth: 3,
    paddingLeft: 10,
    marginTop: 0,
    marginBottom: RICH_HTML_BLOCK_SPACING,
  },
  img: {
    maxWidth: RICH_HTML_IMG_MAX_WIDTH,
    maxHeight: RICH_HTML_IMG_MAX_HEIGHT,
    height: 'auto',
  },
  ul: {
    marginTop: 0,
    marginBottom: RICH_HTML_BLOCK_SPACING,
    paddingLeft: 22,
  },
  ol: {
    marginTop: 0,
    marginBottom: RICH_HTML_BLOCK_SPACING,
    paddingLeft: 22,
  },
  li: {
    marginBottom: RICH_HTML_LIST_ITEM_SPACING,
  },
  h1: {
    fontSize: 22,
    fontWeight: '700',
    marginTop: 0,
    marginBottom: RICH_HTML_HEADING_SPACING,
  },
  h2: {
    fontSize: 18,
    fontWeight: '700',
    marginTop: 0,
    marginBottom: RICH_HTML_HEADING_SPACING,
  },
  h3: {
    fontSize: 16,
    fontWeight: '700',
    marginTop: 0,
    marginBottom: RICH_HTML_HEADING_SPACING,
  },
  a: {
    color: RICH_HTML_LINK_COLOR,
    textDecorationLine: 'underline',
  },
  mark: {
    backgroundColor: RICH_HTML_MARK_BG,
  },
  table: {
    marginTop: 8,
    marginBottom: 8,
    flexDirection: 'column',
    alignSelf: 'flex-start',
  },
  thead: {
    flexDirection: 'column',
  },
  tbody: {
    flexDirection: 'column',
  },
  tfoot: {
    flexDirection: 'column',
  },
  tr: {
    flexDirection: 'row',
    alignItems: 'stretch',
  },
  pre: {
    backgroundColor: '#F3F4F6',
    color: '#111827',
    fontFamily: 'monospace',
    padding: 8,
    borderRadius: 4,
    marginBottom: 8,
  },
  code: {
    backgroundColor: '#F3F4F6',
    fontFamily: 'monospace',
    borderRadius: 3,
    paddingHorizontal: 4,
  },
  th: {
    borderWidth: 0,
    backgroundColor: RICH_HTML_TABLE_HEADER_BG,
    paddingTop: 3,
    paddingBottom: 3,
    paddingLeft: 0,
    paddingRight: 16,
    fontWeight: '700',
    minWidth: 80,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 80,
  },
  td: {
    borderWidth: 0,
    paddingTop: 6,
    paddingBottom: 6,
    paddingLeft: 8,
    paddingRight: 8,
    minWidth: 80,
    flexGrow: 0,
    flexShrink: 0,
    flexBasis: 80,
  },
};

/**
 * Extra class on a request view-mode field. Two classes beat the default
 * `.ss-rich-html-view` color, so a message on the same page stays on
 * `RICH_HTML_BODY_COLOR`.
 */
export const RICH_HTML_VIEW_FIELD_CLASS = 'ss-rich-html-view-field';

export function richHtmlViewClassName(viewField: boolean): string {
  return viewField
    ? `ss-rich-html-view ${RICH_HTML_VIEW_FIELD_CLASS}`
    : 'ss-rich-html-view';
}

/** Real-DOM view styles so web view-only HTML matches the editor contract. */
export const VIEW_RICH_HTML_CSS = `
.ss-rich-html-view {
  color: ${RICH_HTML_BODY_COLOR};
  font-size: ${RICH_HTML_BODY_FONT_SIZE}px;
  line-height: ${RICH_HTML_BODY_LINE_HEIGHT}px;
  max-width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-wrap: anywhere;
  word-break: break-word;
}
${richHtmlScopedCss('.ss-rich-html-view')}
${mentionTokenCss('.ss-rich-html-view')}
.ss-rich-html-view.${RICH_HTML_VIEW_FIELD_CLASS} {
  color: ${colors.textSecondary};
}
`;

export function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

/**
 * The least of the DOM a click resolution needs, so the resolver is testable
 * on a jsdom tree without a global `Element`.
 */
export interface RichHtmlClickTarget {
  closest(selector: string): RichHtmlClickTarget | null;
  getAttribute(name: string): string | null;
}

export interface RichHtmlClickContainer {
  querySelectorAll(selector: string): ArrayLike<unknown>;
}

export type RichHtmlClick =
  | { kind: 'image'; src: string; ordinal: number }
  | { kind: 'link'; href: string };

/**
 * What a click inside a rendered HTML view means (#1530, web).
 *
 * THE ANCHOR WINS. An `<img>` inside an `<a>` is a linked picture, and the
 * link is what the author put there; so `closest('a')` is asked FIRST and a
 * linked image follows its link rather than opening the viewer.
 *
 * THE ORDINAL, NOT THE SRC. Two pictures in one body can carry the same
 * thumbnail bytes (two uploads of one screenshot), so a src cannot say which
 * one was clicked. The image is named by its position among the container's
 * `<img>` nodes in document order, the same order the body's HTML lists them.
 */
export function resolveRichHtmlClick(
  target: RichHtmlClickTarget,
  container: RichHtmlClickContainer,
  imagesPressable: boolean,
): RichHtmlClick | null {
  const anchor = target.closest('a');
  const href = anchor?.getAttribute('href');
  if (href) {
    return { kind: 'link', href };
  }
  if (!imagesPressable) {
    return null;
  }
  const image = target.closest('img');
  const src = image?.getAttribute('src');
  if (!image || !src) {
    return null;
  }
  const ordinal = Array.prototype.indexOf.call(
    container.querySelectorAll('img'),
    image,
  );
  return { kind: 'image', src, ordinal };
}

/** The least of a render-html `TNode` the ordinal walk needs. */
export interface RichHtmlImageNode {
  readonly tagName: string | null;
  readonly parent?: RichHtmlImageNode | null;
  readonly children: ReadonlyArray<RichHtmlImageNode>;
}

/**
 * Which `<img>` of the document a tapped image node is, in document order
 * (#1530, native): walk to the root, then count `img` nodes depth-first until
 * the tapped node is reached. `-1` when the node is not in its own tree, which
 * cannot happen but is the honest answer if it does.
 */
export function imageOrdinalForNode(node: RichHtmlImageNode): number {
  let root: RichHtmlImageNode = node;
  while (root.parent) {
    root = root.parent;
  }
  let ordinal = -1;
  const found = (current: RichHtmlImageNode): boolean => {
    if (current.tagName === 'img') {
      ordinal += 1;
      if (current === node) return true;
    }
    for (const child of current.children) {
      if (found(child)) return true;
    }
    return false;
  };
  return found(root) ? ordinal : -1;
}

/** `''`, `<p></p>` and `<p><br></p>` all mean the user typed nothing. */
export function isEmptyRichHtml(value: string | null | undefined): boolean {
  if (value == null) {
    return true;
  }
  if (/<img\b/i.test(value)) {
    return false;
  }
  return (
    value
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim() === ''
  );
}

export function toRenderableRichHtml(value: string): string {
  if (!value?.trim()) {
    return '';
  }
  if (isLegacyPlainTextAreaValue(value)) {
    return `<p>${escapeHtml(value).replace(/\n/g, '<br />')}</p>`;
  }
  return sanitizeTextArea(value, { allowRemoteHttpImages: true });
}

/** Never hand `sitescope-attachment://` to native Image / RenderHTML. */
export function htmlSafeForNativeImage(html: string): string {
  return placeholderUnresolvedAttachmentImages(
    rewriteHtmlAttachmentRefsToPreviewSrcs(html),
  );
}

/**
 * Browser `<img src="sitescope-attachment://…">` triggers a failed GET and,
 * on public origins (DEVSECURE), Chrome's local-network permission prompt
 * because a numeric host like `9` parses as IPv4 `0.0.0.9`.
 * Known previews become data URLs; leftover custom schemes and local URLs
 * are blanked.
 */
export function omitUndisplayableAttachmentSrcs(html: string): string {
  return html.replace(
    /(<img\b[^>]*\bsrc=)(["'])([^"']+)\2/gi,
    (full, prefix: string, quote: string, src: string) => {
      const preview = pendingInlineImagePreviewSrc(src);
      if (
        isDisplayableInlineImageSrc(preview) &&
        !isLocalNetworkImgSrc(preview)
      ) {
        return `${prefix}${quote}${preview}${quote}`;
      }
      if (
        /^sitescope-attachment(?:-pending)?:/i.test(src) ||
        isLocalNetworkImgSrc(src)
      ) {
        return `${prefix}${quote}${quote}`;
      }
      return full;
    },
  );
}

/** Web keeps `<img>` tags so the photo can render; native placeholders until a data URL exists. */
export function htmlForRichHtmlDisplay(html: string): string {
  const withPreviews = rewriteHtmlAttachmentRefsToPreviewSrcs(html);
  if (Platform.OS === 'web') {
    return omitUndisplayableAttachmentSrcs(withPreviews);
  }
  return placeholderUnresolvedAttachmentImages(withPreviews);
}

function isPositiveAttachmentId(value: unknown): value is number {
  return typeof value === 'number' && Number.isInteger(value) && value > 0;
}

async function resolveAttachmentDisplaySrc(
  attachmentId: number,
): Promise<string | null> {
  if (!isPositiveAttachmentId(attachmentId)) {
    return null;
  }
  // Inline preview is thumbnail-only. Full `getAttachment` is a 10-minute
  // Base64 download — that is the ~10s hang on large photos.
  // Built from server metadata (`data:${thumbnailMimeType};base64,…`) and
  // spliced into already-sanitized HTML here and via the preview map.
  const thumbnailSrc = await getAttachmentThumbnail(attachmentId);
  if (!thumbnailSrc || !isRasterImageDataUrl(thumbnailSrc)) {
    return null;
  }
  rememberPendingInlineImagePreview(
    `sitescope-attachment://${attachmentId}`,
    thumbnailSrc,
  );
  return thumbnailSrc;
}

export async function displaySrcForInlineImage(
  src: string,
): Promise<string | null> {
  const preview = pendingInlineImagePreviewSrc(src);
  if (isDisplayableInlineImageSrc(preview)) {
    return preview;
  }
  const match = /^sitescope-attachment:\/\/(\d+)\/?$/.exec(src.trim());
  if (!match) {
    return null;
  }
  const attachmentId = Number.parseInt(match[1], 10);
  if (!Number.isInteger(attachmentId) || attachmentId <= 0) {
    return null;
  }
  return resolveAttachmentDisplaySrc(attachmentId);
}

/** Load persisted inline images into the preview map so editors can show them. */
export function useHydrateInlineImagePreviews(
  html: string,
  requestId?: string,
): number {
  const [generation, setGeneration] = useState(0);

  useEffect(() => {
    let active = true;
    const attachmentIds = collectRichHtmlAttachmentIds(html);
    if (attachmentIds.length === 0) {
      return;
    }

    Promise.all(attachmentIds.map(id => resolveAttachmentDisplaySrc(id))).then(
      srcs => {
        if (!active || !srcs.some(src => src != null)) {
          return;
        }
        setGeneration(value => value + 1);
      },
    );

    return () => {
      active = false;
    };
  }, [html, requestId]);

  return generation;
}

/**
 * The HTML handed to `dangerouslySetInnerHTML` (web) or the WebView (native).
 * Preview and thumbnail sources are spliced in AFTER `toRenderableRichHtml`, so
 * the sanitizer runs once more as the very last step; it keeps the image
 * sources the display needs (raster data URLs, blob:/file: previews, and the
 * empty `src` placeholders whose position sets an image's click ordinal).
 */
export function finalizeRichHtmlForDisplay(html: string): string {
  return sanitizeTextArea(htmlForRichHtmlDisplay(html), {
    allowRemoteHttpImages: true,
    allowDisplayImageSrcs: true,
  });
}

export function useResolvedRichHtml(
  value: string,
  requestId?: string,
): { safeHtml: string; resolvedHtml: string } {
  const safeHtml = useMemo(() => toRenderableRichHtml(value), [value]);
  const finalSafeHtml = useMemo(
    () => finalizeRichHtmlForDisplay(safeHtml),
    [safeHtml],
  );
  const [resolvedHtml, setResolvedHtml] = useState(finalSafeHtml);

  useEffect(() => {
    let active = true;
    setResolvedHtml(finalSafeHtml);

    const attachmentIds = collectRichHtmlAttachmentIds(safeHtml);
    if (attachmentIds.length === 0) {
      return () => {
        active = false;
      };
    }

    Promise.all(
      attachmentIds.map(async attachmentId => ({
        attachmentId,
        src: await resolveAttachmentDisplaySrc(attachmentId),
      })),
    )
      .then(resolutions => {
        if (!active) {
          return;
        }
        const nextHtml = resolutions.reduce((html, resolution) => {
          if (!resolution.src && Platform.OS === 'web') {
            return html;
          }
          return replaceRichHtmlAttachmentSrc(
            html,
            resolution.attachmentId,
            resolution.src,
          );
        }, safeHtml);
        setResolvedHtml(finalizeRichHtmlForDisplay(nextHtml));
      })
      .catch(() => {
        if (active) {
          setResolvedHtml(finalSafeHtml);
        }
      });

    return () => {
      active = false;
    };
  }, [finalSafeHtml, requestId, safeHtml]);

  return { safeHtml, resolvedHtml };
}
